/* Copyright (C) 2023 Advenio Tecnosys Pvt. Ltd.

*        All Rights Reserved

*

 * NOTICE: 

 * All information contained herein is, and remains

* the property of Advenio Tecnosys and its suppliers,

* if any.  The intellectual and technical concepts contained

* herein are proprietary to Advenio Tecnosys

* and its suppliers and may be covered by Indian, U.S. and Foreign Patents,

* patents in process, and are protected by trade secret or copyright law.

* Appropriation, redistribution, dissemination of this information or reproduction

* of this material is strictly forbidden unless prior written permission is obtained

* from Advenio Tecnosys Pvt. Ltd.

*

* Author: < Ravi Ranjan Kumar, Sanket, Mylavarapu Sarath Sai, Amal Sabu, Ujjwal Singh >

*/

import Dexie from "dexie";
// import S3 from "aws-s3";
// import axios from "axios";
// import { baseURL, syncBaseUrl } from "database/onlineDb/baseUrl";
import { differenceObject } from "common/common-util-functions";
import { v4 as uuid } from "uuid";
import jwt from "jsonwebtoken";
import { getAuthTokenFromSessionStorage } from "helpers/auth-session";
// import startAndManageCreateUrlToImageFileAndThumbnailWorker from "helpers/workers";
import {
  getImageAspectRatio,
  getImageAspectRatioImage,
  urlToImageFileAndThumbnail,
} from "helpers/common";

const dbName = "AdvenioDb";
const dbVersion = 5;

let db;

const getActiveOrgId = () => {
  const token = getAuthTokenFromSessionStorage();
  try {
    return jwt.decode(token)?.orgz_id ?? null;
  } catch (error) {
    return null;
  }
};

const getActiveBulkImages = async (activeOrgId = getActiveOrgId()) => {
  if (activeOrgId === null || activeOrgId === undefined) return [];
  const rows = await db.bulkImagesData.toArray();
  return rows.filter((row) => row.orgz_id === activeOrgId);
};

db = new Dexie(dbName);
db.version(2).stores({
  patients: "mrn,firstName,mobileNo,regDate,email,dob,age",
  users: "++user_id,fname,mob_no,imodon,email,pwd,orgz_id",
  bulkImagesData:
    "guid,info,file,originalDimension,imodon,quality,resizedDimension,status",
  viewImagesIDB:
    "guid,info,file,imodon,quality,status,consent,drConsentDetails",
  reviewImages: "guid,info,file,imodon,quality,status,consent,drConsentDetails",
  reportContent: "name,AdviceData,acronym,reports",
  viewPatients: "mrn,firstName,mobileNo,email,dob,age",
  viewPatientsBu:
    "patientId,fname,lname,gender,age,address,phone,created_on,lmodon",
  userSettings:
    "user_id, algorithms, cameraModel, cameraType, cameraVendor, imgDimensions,inputFileLimit, inputFileSize, inputFilesBulk, isDrConsentEnabled, themeName",
  reviewPatients: "mrn,firstName,mobileNo,email,dob,age",
  doctorAnnotationImages:
    "guid,info,file,imodon,quality,status,consent,drConsentDetails",
  doctorAnnotationPatient: "mrn,firstName,mobileNo,email,dob,age",
  totalBuPatientsCount: "totalBuPatientsCount",
  totalPatientsCount: "totalPatientsCount",
  allViewPatientsMrnsList: "mrnsList, allViewPatientsMrnsList",
  totalBulkImagesCount: "totalBulkImagesCount",
  totalAnnotationPatientsCount: "totalAnnotationPatientsCount",
});
db.version(dbVersion).stores({
  patients: "mrn,firstName,mobileNo,regDate,email,dob,age",
  users: "++user_id,fname,mob_no,imodon,email,pwd,orgz_id",
  bulkImagesData:
    "guid,info,file,originalDimension,imodon,quality,resizedDimension,status",
  viewImagesIDB:
    "guid,info,file,imodon,quality,status,consent,drConsentDetails",
  reviewImages: "guid,info,file,imodon,quality,status,consent,drConsentDetails",
  reportContent: "name,AdviceData,acronym,reports",
  viewPatients: "mrn,firstName,mobileNo,email,dob,age",
  viewPatientsBu:
    "patientId,fname,lname,gender,age,address,phone,created_on,lmodon",
  userSettings:
    "user_id, algorithms, cameraModel, cameraType, cameraVendor, imgDimensions,inputFileLimit, inputFileSize, inputFilesBulk, isDrConsentEnabled, themeName",
  reviewPatients: "mrn,firstName,mobileNo,email,dob,age",
  doctorAnnotationImages:
    "guid,info,file,imodon,quality,status,consent,drConsentDetails",
  doctorAnnotationPatient: "mrn,firstName,mobileNo,email,dob,age",
  //count for pagingation
  totalBuPatientsCount: "totalBuPatientsCount",
  totalPatientsCount: "totalPatientsCount",
  allViewPatientsMrnsList: "mrnsList, allViewPatientsMrnsList",
  totalBulkImagesCount: "totalBulkImagesCount",
  totalAnnotationPatientsCount: "totalAnnotationPatientsCount",
  entitlement: "orgz_id, projection",
});
db.version(5).stores({
  entitlement: "orgz_id, projection",
});

const createDb = async () => {
  db = new Dexie(dbName);
  db.version(5).stores({
    patients: "mrn,firstName,mobileNo,regDate,email,dob,age",
    users:
      "user_id, email, exp, expertise, fname, job_title, lname, logo, mname, mob_no, orgz_code, orgz_id, orgz_name, prim_role, prim_specialization, prof_img, profile, qualification, scnd_role, scnd_specialization",
    bulkImagesData: "guid,info,file,imodon,quality,status",
    viewImagesIDB:
      "guid,info,file,imodon,quality,status,consent,drConsentDetails",
    reviewImages:
      "guid,info,file,imodon,quality,status,consent,drConsentDetails",
    reportContent: "name,AdviceData,acronym,reports",
    viewPatients: "mrn,firstName,mobileNo,email,dob,age",
    viewPatientsBu:
      "patientId,fname,lname,gender,age,address,phone,created_on,lmodon",
    userSettings:
      "user_id, algorithms, cameraModel, cameraType, cameraVendor, imgDimensions,inputFileLimit, inputFileSize, inputFilesBulk, isDrConsentEnabled, themeName",
    reviewPatients: "mrn,firstName,mobileNo,email,dob,age",
    doctorAnnotationImages:
      "guid,info,file,imodon,quality,status,consent,drConsentDetails",
    doctorAnnotationPatient: "mrn,firstName,mobileNo,email,dob,age",
    totalBuPatientsCount: "totalBuPatientsCount",
    totalPatientsCount: "totalPatientsCount",
    allViewPatientsMrnsList: "mrnsList, allViewPatientsMrnsList",
    totalBulkImagesCount: "totalBulkImagesCount",
    totalAnnotationPatientsCount: "totalAnnotationPatientsCount",
    entitlement: "orgz_id, projection",
  });
};
/* eslint-disable no-return-await */
const dbExist = async () => {
  const isDbExist = await Dexie.exists("AdvenioDb")
    .then(function (exists) {
      if (exists) {
        // console.log("Database exists");
        return true;
      } else {
        // console.log("Database doesn't exist");
        return false;
      }
    })
    .catch(function (error) {
      console.error(
        "Oops, an error occurred when trying to check database existance"
      );
      return false;
    });
  return isDbExist;
};
// user data start

const refreshIdb = async () => {
  try {
    await db.delete();
    await createDb();
  } catch (error) {
    console.log("error while refreshing the indexeddb", error);
  }
};

const saveUserDetail = async (userData) => {
  const users = await db.users
    .put({ ...userData, user_id: userData.user_id })
    .then((res) => {
      return res;
    })
    .catch(function (error) {
      console.log("error while saving user to indexedDb", error);
    });
  return users;
};
const updateUserDetail = async (id, userData) => {
  let updateUserDataRes = await db.users
    .update(id, userData)
    .then(function (updated) {
      if (updated) {
        console.log("user updated", updated);
        return;
      }

      console.log(
        `Nothing was updated - there were no user with primary key:${id}`
      );
    })
    .catch((err) => {
      console.log("updateUserDetail err", err);
    });
  return updateUserDataRes;
};

const getUserSettingsFromIdb = async () => {
  const res = { data: [] };
  if (db.userSettings === undefined) {
    return res;
  }
  const usersFromIdb = await db.userSettings
    .toArray()
    .then((res) => {
      return { data: res !== undefined ? res : [] };
    })
    .catch((err) => {
      return { error: err };
    });

  return usersFromIdb?.data || [];
};

const saveUserSettings = async (userSettingsToSave) => {
  const clearUserSettingsRes = await db.userSettings.clear();
  const updatedUserSettingsIdbRes = await db.userSettings
    .put({ ...userSettingsToSave, user_id: userSettingsToSave.user_id })
    .then((res) => {
      return res;
    })
    .catch(function (error) {
      console.log("error while saving user to indexedDb", error);
    });
  return updatedUserSettingsIdbRes;
};

const updateUserSettingsIdb = async (id, userSettingsToSave) => {
  const updatedUserSettingsIdbRes = await db.userSettings
    .update(id, userSettingsToSave)
    .then(function (updated) {
      if (updated) {
        console.log("user updated", updated);
        return;
      }

      console.log(
        `Nothing was updated - there were no user with primary key:${id}`
      );
    })
    .catch((err) => {
      console.log("updateUserSettings err", err);
    });
  return updatedUserSettingsIdbRes;
};

const getUsers = async () => {
  const res = { data: [] };
  if (db.users === undefined) {
    return res;
  }
  const users = await db.users
    .toArray()
    .then((res) => {
      return { data: res !== undefined ? res : [] };
    })
    .catch((err) => {
      return { error: err };
    });

  return users?.data || [];
};

const deleteUser = async (id) => await db.users.delete(id);

// user data end

const getPatients = async () => {
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.patients === undefined) {
    return res;
  }
  let patients = await db.patients
    .toArray()
    .then(async (res) => {
      return { data: res !== undefined ? res : [] };
    })
    .catch((err) => {
      return { error: err };
    });
  return patients;
};

const getPatientDetailById = async (id) => {
  // console.log('patientbyId==>indexdb',id)
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.patients === undefined) {
    return res;
  }
  let patients = await db.patients
    .get(id)
    .then((res) => {
      // console.log('patientbyId==>indexdb',res)
      return { data: res };
    })
    .catch((err) => {
      return { errors: err };
    });

  return patients;
};

const deletePatient = async (id) => await db.patients.delete(id);

const savePatient = async (patientData) => {
  let addPatientRes = await db.patients
    .put({ ...patientData, mrn: uuid(), isRegOnServer: false })
    .then((res) => {
      // Then when data is stored, read from it
      // return db.patients.get('mrn');
      return res;
    })
    .catch(function (error) {
      //    alert ("Ooops: " + error);
    });
  // console.log('addPatientRes',addPatientRes)
  return addPatientRes;
};
const updatePatient = async (patientData) => {
  //   console.log("patientData", patientData);
  let addPatientRes = {};
  let oldpatientData = await getPatientDetailById(patientData.mrn).then(
    (res) => {
      return res.data;
    }
  );
  // advenio-qocm — a patient that exists only on the server (deep link,
  // ko7t adopt after a ghost-row purge) has no local Dexie row to diff
  // against: detailedDiff(payload, undefined) yields {} and Table.update()
  // resolves 0 for a missing key, so requirePatientWrite threw "patient
  // snapshot persistence failed" and the reservation chain died before
  // confirm. Upsert instead so the write lands either way.
  if (oldpatientData === undefined || oldpatientData === null) {
    addPatientRes = await db.patients
      .put({ ...patientData, mrn: patientData.mrn })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
    return addPatientRes;
  }
  let diffData = differenceObject(patientData, oldpatientData);
  let updated = diffData.updated !== undefined ? diffData.updated : {};
  if (updated.registrationData !== undefined) {
    addPatientRes = await db.patients
      .update(patientData.mrn, { ...patientData, basicDetailOnServer: false })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else if (
    updated.personalData !== undefined ||
    updated.patientHistoryData !== undefined ||
    updated.lifeStyleData !== undefined ||
    updated.clinicalConditionData !== undefined
  ) {
    // alert('rest')

    addPatientRes = await db.patients
      .update(patientData.mrn, { ...patientData, restDetailOnServer: false })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else if (updated.retinalImagesData !== undefined) {
    // alert('image')
    addPatientRes = await db.patients
      .update(patientData.mrn, {
        ...patientData,
        imgDataOnServer: false,
        imgOnServer: false,
      })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else {
    addPatientRes = await db.patients
      .update(patientData.mrn, { ...patientData })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  }
  //    console.log('addPatientRes',addPatientRes)
  return addPatientRes;
};

const updateViewPatient = async (patientData) => {
  let addPatientRes = {};
  let oldpatientData = await getViewPatients(patientData.mrn).then((res) => {
    return res.data;
  });

  let diffData = differenceObject(patientData, oldpatientData);
  let updated = diffData.updated !== undefined ? diffData.updated : {};
  if (updated.registrationData !== undefined) {
    addPatientRes = await db.viewPatients
      .update(patientData.mrn, { ...patientData, basicDetailOnServer: false })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else if (
    updated.personalData !== undefined ||
    updated.patientHistoryData !== undefined ||
    updated.lifeStyleData !== undefined ||
    updated.clinicalConditionData !== undefined
  ) {
    // alert('rest')
    addPatientRes = await db.viewPatients
      .update(patientData.mrn, { ...patientData, restDetailOnServer: false })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else if (updated.retinalImagesData !== undefined) {
    // alert('image')
    addPatientRes = await db.viewPatients
      .update(patientData.mrn, {
        ...patientData,
        // imgDataOnServer: false,
        // imgOnServer: false,
      })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else {
    addPatientRes = await db.viewPatients
      .update(patientData.mrn, { ...patientData })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  }

  return addPatientRes;
};

const updatePatientID = async (mrn, data) => {
  let addPatientRes = await db.patients
    .put({ ...data, mrn: mrn })
    .then((res) => {
      return res;
    })
    .catch(function (error) {});
  return addPatientRes;
};
const updateViewPatientID = async (mrn, data) => {
  let patient = await getViewPatients(mrn).then((res) => {
    return res.data;
  });

  let addViewPatientRes = await db.viewPatients
    .put({ ...patient, ...data, mrn: mrn })
    .then((res) => {
      return res;
    })
    .catch(function (error) {});
  return addViewPatientRes;
};

const deleteAllViewPatients = async () => await db.viewPatients.clear();

const deleteViewPatientById = async (ids) => {
  await db.viewPatients.bulkDelete(ids);
  const oldTotalPatientsCount = await db.totalPatientsCount.toArray();
  await db.totalPatientsCount.clear();
  await db.totalPatientsCount.put({
    totalPatientsCount:
      oldTotalPatientsCount[0]?.totalPatientsCount - ids.length,
  });
  return await getAllViewPatients();
};

//    bulkImage upload db start
const saveBulkImages = async (imgList) => {
  const activeOrgId = getActiveOrgId();
  const finalData = [];
  for (let i = 0; i < imgList.length; ++i) {
    const existingStatus = imgList[i]?.status;
    const status = existingStatus || "Saved";
    finalData.push({ ...imgList[i], orgz_id: activeOrgId, status });
  }
  return db.transaction("rw", db.bulkImagesData, async () => {
    await db.bulkImagesData.bulkAdd(finalData);
    const saved = await getActiveBulkImages(activeOrgId);
    return { data: saved };
  });
};
const getImages = async () => {
  const res = { data: [] };

  if (db.bulkImagesData === undefined) {
    return res;
  }
  const imgList = await getActiveBulkImages();
  return { data: imgList };
};
const updateImageDataByIDForBulkUpload = async (guid, data) => {
  try {
    const updateImageDataRes = await db.bulkImagesData.update(guid, {
      ...data,
      orgz_id: getActiveOrgId(),
    });
    return updateImageDataRes;
  } catch (error) {
    console.log("err", error);
  }
};
const updateImageDataByID = async (guid, data) => {
  try {
    const updateImageDataRes = await db.viewImagesIDB.update(guid, data);
    return updateImageDataRes;
  } catch (error) {
    console.log("err", error);
  }
};
const getImageById = async (id) => {
  // console.log('patientbyId==>indexdb',id)
  const res = { data: [] };
  // console.log('db',db.patients)
  if (db.bulkImagesData === undefined) {
    return res;
  }
  const ImageData = await db.bulkImagesData
    .get(id)
    .then((res) => {
      // console.log('bulkImagesData==>indexdb',res)
      return { data: res };
    })
    .catch((err) => {
      return { errors: err };
    });
  // console.log('ImageData',ImageData)
  return ImageData;
};
const getImagesByStatus = async (status) => {
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.bulkImagesData === undefined) {
    return res;
  }
  let imgList = await db.bulkImagesData
    .toArray()
    .then((res) => {
      // console.log('patients',res)
      let filteredData = res.filter((item) => item.status === status);
      return { data: filteredData !== undefined ? filteredData : [] };
    })
    .catch((err) => {
      return { error: err };
    });

  return imgList;
};
const deleteImageById = async (id) => await db.bulkImagesData.delete(id);

const bulKDeleteImages = async (imglist) => {
  let keys = [];
  imglist.map((item) => {
    keys.push(item);
  });
  let deletedBulkImgRes = db.bulkImagesData
    .bulkDelete(keys)
    .then((res) => {
      // console.log('deleted')
    })
    .catch(Dexie.BulkError, function (e) {
      // Explicitely catching the bulkAdd() operation makes those successful
      // additions commit despite that there were errors.
      console.error();
    });
  return deletedBulkImgRes;
};
// bulkImage upload db end

//  view-images upload db start

const getViewImages = async () => {
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.viewImagesIDB === undefined) {
    return res;
  }
  let imgList = await db.viewImagesIDB.toArray().then(async (res) => {
    return { data: res.length !== 0 ? res : [] };
  });
  return imgList;
};

const updateViewImageDataByID = async (guid, data) => {
  //   console.log("data", data);
  let updateImageDataRes = await db.viewImagesIDB
    .update(guid, data)
    .then
    // We don't wnat this log in the console in the demo version, author:Sai
    //   function (updated) {
    //   if (updated) console.log("updated");
    //   else
    //     console.log(
    //       "Nothing was updated - there were no friend with primary key: 2"
    //     );
    // }
    ()
    .catch((err) => {
      console.log("err", err);
    });
  return updateImageDataRes;
};

const getViewImageById = async (id) => {
  // console.log('patientbyId==>indexdb',id)
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.viewImagesIDB === undefined) {
    return res;
  }
  let ImageData = await db.viewImagesIDB
    .get(id)
    .then((res) => {
      // console.log('viewImagesIDB==>indexdb',res)
      return { data: res };
    })
    .catch((err) => {
      return { errors: err };
    });
  // console.log('ImageData',ImageData)
  return ImageData;
};

const getViewImagesByStatus = async (status) => {
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.viewImagesIDB === undefined) {
    return res;
  }
  let imgList = await db.viewImagesIDB
    .toArray()
    .then((res) => {
      // console.log('patients',res)
      let filteredData = res.filter((item) => item.status === status);
      return { data: filteredData !== undefined ? filteredData : [] };
    })
    .catch((err) => {
      return { error: err };
    });

  return imgList;
};

const deleteViewImageById = async (id) => await db.viewImagesIDB.delete(id);

const deleteViewImages = async (imglist) => {
  let keys = [];
  imglist.map((item) => {
    keys.push(item);
  });
  let deletedBulkImgRes = await db.viewImagesIDB.bulkDelete(keys);
  const oldImagesCount = await db.totalBulkImagesCount.toArray();

  await db.totalBulkImagesCount.clear();
  await db.totalBulkImagesCount.put({
    totalBulkImagesCount: oldImagesCount[0]?.totalBulkImagesCount - keys.length,
  });

  return deletedBulkImgRes;
};

const deleteAllViewImages = async () => await db.viewImagesIDB.clear();

const swapViewImgList = async (imgList) => {
  try {
    await db.viewImagesIDB.clear();
    await db.viewImagesIDB.bulkPut(imgList);
    const savedList = await getViewImages();
    return savedList.data || [];
  } catch (error) {
    console.log(
      `There was an error while storing data in indexDB, error: ${error}`
    );
    throw error; // optional - rethrow the error to be handled by the caller
  }
};

// view-images upload db end

//report content start
const saveReportContent = async (contentList) => {
  let finalData = [];
  for (let i = 0; i < contentList.length; ++i) {
    finalData.push({ ...contentList[i], imo: new Date() });
  }
  let saveReportContentRes = db.reportContent
    .bulkAdd(finalData)
    .then(function (lastKey) {
      // let savedList=getImages()
      return lastKey;
    })
    .catch(Dexie.BulkError, function (e) {
      // Explicitely catching the bulkAdd() operation makes those successful
      // additions commit despite that there were errors.
      console.error();
    });
  return saveReportContentRes;
};

const getReportContentList = async () => {
  let res = { data: [] };
  if (db.reportContent === undefined) {
    return res;
  }
  let reportContentList = await db.reportContent.toArray().then(async (res) => {
    return { data: res.length !== 0 ? res : [] };
  });
  return reportContentList;
};

const saveViewPatients = async (patientData) => {
  let addPatientRes = await db.viewPatients

    .put({ ...patientData, mrn: patientData.mrn, isRegOnServer: true })
    .then((res) => {
      // Then when data is stored, read from it
      // return db.patients.get('mrn');
      return res;
    })
    .catch(function (error) {
      //    alert ("Ooops: " + error);
    });
  // console.log('addPatientRes',addPatientRes)
  return addPatientRes;
};

const getAllViewPatients = async () => {
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.viewPatients === undefined) {
    return res;
  }
  let patients = await db.viewPatients.toArray().then(async (res) => {
    if (res !== undefined) {
      const descendingOrderViewPatientsMrnWise = res.sort((a, b) => {
        return b.mrn - a.mrn;
      });
      return { data: descendingOrderViewPatientsMrnWise };
    } else {
      return { data: [] };
    }
  });
  return patients;
};

const getViewPatients = async (id) => {
  // console.log('patientbyId==>indexdb',id)
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.patients === undefined) {
    return res;
  }
  let patients = await db.viewPatients
    .get(id)
    .then((res) => {
      //  console.log("patientbyId==>indexdb", res);
      return { data: res };
    })
    .catch((err) => {
      return { errors: err };
    });

  return patients;
};

//report content end

//review patients

const saveReviewPatients = async (patientData) => {
  let addPatientRes = await db.reviewPatients

    .put({ ...patientData, mrn: patientData.mrn, isRegOnServer: true })
    .then((res) => {
      // Then when data is stored, read from it
      // return db.patients.get('mrn');
      return res;
    })
    .catch(function (error) {
      //    alert ("Ooops: " + error);
    });
  // console.log('addPatientRes',addPatientRes)
  return addPatientRes;
};
const getAllReviewPatients = async () => {
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.reviewPatients === undefined) {
    return res;
  }
  let patients = await db.reviewPatients
    .toArray()
    .then(async (res) => {
      if (res !== undefined) {
        const ascendingOrderReviewPatientsMrnWise = res.sort((a, b) => {
          const lastModifedDateForA =
            a.right?.[0]?.data?.lmodon || a.right?.[0]?.data?.lmodon;
          const lastModifedDateForB =
            b.left?.[0]?.data?.lmodon || b.right?.[0]?.data?.lmodon;
          return (
            new Date(lastModifedDateForA).getTime() -
            new Date(lastModifedDateForB).getTime()
          );
        });
        return { data: ascendingOrderReviewPatientsMrnWise };
      } else {
        return { data: [] };
      }

      // return { data: res !== undefined ? res : [] };
    })
    .catch((err) => {
      return { error: err };
    });
  return patients;
};

const getReviewPatient = async (id) => {
  // console.log('patientbyId==>indexdb',id)
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.patients === undefined) {
    return res;
  }
  let patients = await db.viewPatients
    .get(id)
    .then((res) => {
      //  console.log("patientbyId==>indexdb", res);
      return { data: res };
    })
    .catch((err) => {
      return { errors: err };
    });

  return patients;
};

const deleteAllReviewPatients = async () => await db.reviewPatients.clear();

const updateReviewPatient = async (patientData) => {
  let addPatientRes = {};
  let oldpatientData = await getReviewPatient(patientData.mrn).then((res) => {
    return res.data;
  });

  let diffData = differenceObject(patientData, oldpatientData);
  let updated = diffData.updated !== undefined ? diffData.updated : {};
  if (updated.registrationData !== undefined) {
    addPatientRes = await db.reviewPatients
      .update(patientData.mrn, { ...patientData, basicDetailOnServer: false })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else if (
    updated.personalData !== undefined ||
    updated.patientHistoryData !== undefined ||
    updated.lifeStyleData !== undefined ||
    updated.clinicalConditionData !== undefined
  ) {
    // alert('rest')
    addPatientRes = await db.reviewPatients
      .update(patientData.mrn, { ...patientData, restDetailOnServer: false })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else if (updated.retinalImagesData !== undefined) {
    // alert('image')
    addPatientRes = await db.reviewPatients
      .update(patientData.mrn, {
        ...patientData,
        // imgDataOnServer: false,
        // imgOnServer: false,
      })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else {
    addPatientRes = await db.reviewPatients
      .update(patientData.mrn, { ...patientData })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  }

  return addPatientRes;
};

const deleteReviewPatientById = async (id) => {
  await db.reviewPatients.delete(id);
  return await getAllReviewPatients();
};

//review images
const getReviewImages = async () => {
  let res = { data: [] };
  // console.log('db',db.patients)
  if (db.reviewImages === undefined) {
    return res;
  }
  let imgList = await db.reviewImages.toArray().then(async (res) => {
    if (res !== undefined) {
      const ascendingOrderReviewPatientsMrnWise = res.sort((a, b) => {
        return (
          new Date(b.lastModified).getTime() -
          new Date(a.lastModified).getTime()
        );
      });
      return { data: ascendingOrderReviewPatientsMrnWise };
    } else {
      return { data: [] };
    }
  });
  return imgList;
};
const swapReviewImgList = async (imgList) => {
  db.reviewImages
    .clear()
    .then(() => {
      const saveViewImagesRes = db.reviewImages
        .bulkAdd(imgList)
        .then(function (lastKey) {
          const savedList = getReviewImages();
          return savedList;
        })
        .catch(Dexie.BulkError, function (e) {
          // Explicitely catching the bulkAdd() operation makes those successful
          // additions commit despite that there were errors.
          console.error();
        });
      return saveViewImagesRes;
    })
    .catch((err) =>
      console.log(
        `There was an error while storing data in indexDB, err:${err}`
      )
    );
};

const updateReviewImageDataByID = async (guid, data) => {
  console.log(guid, data);
  const updateImageDataRes = await db.reviewImages
    .update(guid, data)
    .then((updated) => {
      if (updated) {
        console.log("updated", updated);
      } else
        console.log(
          "Nothing was updated - there were no friend with primary key: 2"
        );
    })
    .catch((err) => {
      console.log("err", err);
    });
  return updateImageDataRes;
};

const deleteAllReviewImages = async () => await db.reviewImages.clear();

const deleteReviewImageById = async (id) => await db.reviewImages.delete(id);

// doctor-annotation-patient
const saveDoctorAnnotationPatient = async (patientData) => {
  const saveDoctorAnnotationPatientsRes = await db.doctorAnnotationPatient

    .put({ ...patientData, mrn: patientData.mrn, isRegOnServer: true })
    .then((res) => {
      // Then when data is stored, read from it
      // return db.patients.get('mrn');
      return res;
    })
    .catch(function (error) {
      //    alert ("Ooops: " + error);
    });
  // console.log('addPatientRes',addPatientRes)
  return saveDoctorAnnotationPatientsRes;
};
const getAllDoctorAnnotationPatientsFromIDB = async () => {
  const res = { data: [] };
  // console.log('db',db.patients)
  if (db.doctorAnnotationPatient === undefined) {
    return res;
  }
  const allDoctorAnnotationPatientsRes = await db.doctorAnnotationPatient
    .toArray()
    .then(async (res) => {
      if (res) {
        const descendingOrderDocAnnotationPatientsArr = res.sort((a, b) => {
          const patientRegDateForA =
            a.left?.[0]?.data?.patientRegDate ||
            a.right?.[0]?.data?.patientRegDate;
          const patientRegDateForB =
            b.left?.[0]?.data?.patientRegDate ||
            b.right?.[0]?.data?.patientRegDate;
          return (
            new Date(patientRegDateForB).getTime() -
            new Date(patientRegDateForA).getTime()
          );
        });
        return {
          data: descendingOrderDocAnnotationPatientsArr,
        };
      } else {
        return { data: [] };
      }

      // return { data: res !== undefined ? res : [] };
    })
    .catch((err) => {
      return { error: err };
    });
  return allDoctorAnnotationPatientsRes;
};

const getDoctorAnnotationPatient = async (id) => {
  const res = { data: [] };
  if (db.patients === undefined) {
    return res;
  }
  const patient = await db.doctorAnnotationPatient
    .get(id)
    .then((res) => {
      //  console.log("patientbyId==>indexdb", res);
      return { data: res };
    })
    .catch((err) => {
      return { errors: err };
    });

  return patient;
};

const deleteAllDoctorAnnotationPatients = async () =>
  await db.doctorAnnotationPatient.clear();

const updateDoctorAnnotationPatient = async (patientData) => {
  let updateDoctorAnnotationPatientRes = {};
  let oldpatientData = await getDoctorAnnotationPatient(patientData.mrn).then(
    (res) => {
      return res.data;
    }
  );

  let diffData = differenceObject(patientData, oldpatientData);
  let updated = diffData.updated !== undefined ? diffData.updated : {};
  if (updated.registrationData !== undefined) {
    updateDoctorAnnotationPatientRes = await db.doctorAnnotationPatient
      .update(patientData.mrn, { ...patientData, basicDetailOnServer: false })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else if (
    updated.personalData !== undefined ||
    updated.patientHistoryData !== undefined ||
    updated.lifeStyleData !== undefined ||
    updated.clinicalConditionData !== undefined
  ) {
    // alert('rest')
    updateDoctorAnnotationPatientRes = await db.doctorAnnotationPatient
      .update(patientData.mrn, { ...patientData, restDetailOnServer: false })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else if (updated.retinalImagesData !== undefined) {
    // alert('image')
    updateDoctorAnnotationPatientRes = await db.doctorAnnotationPatient
      .update(patientData.mrn, {
        ...patientData,
        // imgDataOnServer: false,
        // imgOnServer: false,
      })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  } else {
    updateDoctorAnnotationPatientRes = await db.doctorAnnotationPatient
      .update(patientData.mrn, { ...patientData })
      .then((res) => {
        return res;
      })
      .catch(function (error) {
        //   alert ("Ooops: " + error);
      });
  }

  return updateDoctorAnnotationPatientRes;
};

const deleteDoctorAnnotationPatientById = async (id) => {
  await db.doctorAnnotationPatient.delete(id);
  return await getAllDoctorAnnotationPatientsFromIDB();
};

// export const viewPatientBuImagesDownloaderAndThumbnailMaker = async (
//   patients
// ) => {
//   const patientsImagesPromises = {};

//   patients.forEach(({ left, right, patientId }) => {
//     if (left?.url) {
//       patientsImagesPromises[patientId + `left`] =
//         startAndManageCreateUrlToImageFileAndThumbnailWorker(left.url);
//     }
//     if (right?.url) {
//       patientsImagesPromises[patientId + `right`] =
//         startAndManageCreateUrlToImageFileAndThumbnailWorker(right.url);
//     }
//   });

//   for (let i = 0; i < patients.length; i++) {
//     const { patientId, right, left } = patients[i];
//     if (right?.url) {
//       const { dataStringUrl, thumbnailUrl } = await patientsImagesPromises[
//         patientId + "right"
//       ];
//       right.url = dataStringUrl;
//       right.thumbnailUrl = thumbnailUrl;
//     }
//     if (left?.url) {
//       const { dataStringUrl, thumbnailUrl } = await patientsImagesPromises[
//         patientId + "left"
//       ];
//       left.url = dataStringUrl;
//       left.thumbnailUrl = thumbnailUrl;
//     }
//   }

//   return patients;
// };

const saveViewPatientsBu = async (patientData) => {
  if (!Array.isArray(patientData)) return [];
  try {
    console.time("time taken to get patients with images");
    let patients = [];
    // if (!window.Worker) {
    for (let i = 0; i < patientData.length; i++) {
      const { left, right } = patientData[i];
      if (left?.url) {
        const { dataStringUrl, thumbnailUrl } =
          await urlToImageFileAndThumbnail(left.url);
        const aspectRatio = await getImageAspectRatio(left.url);
        const [imgHeight,imgWidth]=await getImageAspectRatioImage(left.url)

        patientData[i].left.aspectRatio = aspectRatio;
        patientData[i].left.imgHeight = imgHeight;
        patientData[i].left.imgWidth=imgWidth;
        patientData[i].left.url = dataStringUrl;
        patientData[i].left.thumbnailUrl = thumbnailUrl;
      }
      if (right?.url) {
        const { dataStringUrl, thumbnailUrl } =
          await urlToImageFileAndThumbnail(right.url);
        const aspectRatio = await getImageAspectRatio(right.url);
        const [imgHeight,imgWidth]=await getImageAspectRatioImage(right.url)

        patientData[i].right.aspectRatio = aspectRatio;
        patientData[i].right.imgHeight = imgHeight;
        patientData[i].right.imgWidth=imgWidth;
        patientData[i].right.url = dataStringUrl;
        patientData[i].right.thumbnailUrl = thumbnailUrl;
      }
    }
    patients = patientData;
    // } else {
    //   const patientsFromWorkers =
    //     await viewPatientBuImagesDownloaderAndThumbnailMaker(patientData);
    //   patients = patientsFromWorkers;
    // }
    console.timeEnd("time taken to get patients with images");
    const addPatientRes = await db.viewPatientsBu.bulkPut(patients);
    return addPatientRes;
  } catch (error) {
    console.log("error in saving patient bu to IndexDb", error);
  }
};

const getAllViewPatientsBu = async () => {
  let res = { data: [] };

  if (db.viewPatientsBu === undefined) {
    return res;
  }
  let patients = await db.viewPatientsBu.toArray().then(async (res) => {
    if (res !== undefined) {
      return { data: res };
    } else {
      return { data: [] };
    }
  });
  return patients;
};

const deleteViewPatientBuByIds = async (ids) => {
  await db.viewPatientsBu.bulkDelete(ids);
  const oldTotalBuPatientsCount = await db.totalBuPatientsCount.toArray();

  await db.totalBuPatientsCount.clear();
  await db.totalBuPatientsCount.put({
    totalBuPatientsCount:
      oldTotalBuPatientsCount[0]?.totalBuPatientsCount - ids.length,
  });
  return await getAllViewPatientsBu();
};

const deleteAllViewPatientsBu = async () => await db.viewPatientsBu.clear();

const addOrUpdateTotalBuPatientsCount = async (count) => {
  const users = await db.totalBuPatientsCount
    .put({ totalBuPatientsCount: count })
    .then((res) => {
      return res;
    })
    .catch(function (error) {
      console.log("error while saving count to indexedDb", error);
    });
  return users;
};

const getTotalBuPatientsCount = async () => {
  if (db.totalBuPatientsCount === undefined) {
    return 0;
  }
  let totalBuPatientsCount = await db.totalBuPatientsCount
    .toArray()
    .then(async (res) => {
      if (res !== undefined) {
        return res[0]?.totalBuPatientsCount;
      } else {
        return 0;
      }
    });
  return totalBuPatientsCount;
};

const deleteTotalBuPatientsCount = async () =>
  await db.totalBuPatientsCount.clear();

const addOrUpdateTotalPatientsCount = async (count) => {
  const users = await db.totalPatientsCount
    .put({ totalPatientsCount: count })
    .then((res) => {
      return res;
    })
    .catch(function (error) {
      console.log("error while saving count to indexedDb", error);
    });
  return users;
};
const addOrUpdateAllViewPatientsMrnsList = async (mrnArr) => {
  await db.allViewPatientsMrnsList.clear();
  const allMrnsList = await db.allViewPatientsMrnsList
    .add({ mrnsList: "mrnsList", data: mrnArr })
    .then((res) => {
      return res;
    })
    .catch(function (error) {
      console.log(
        "error while saving all view patients MRN list to indexedDb",
        error
      );
    });
  return allMrnsList;
};

const saveEntitlementProjection = async (orgzId, projection) => {
  try {
    await db.entitlement.put({ orgz_id: Number(orgzId), projection, updatedAt: new Date().toISOString() });
    return true;
  } catch (error) {
    console.log("saveEntitlementProjection error", error);
    return false;
  }
};

const getEntitlementProjection = async (orgzId) => {
  try {
    if (!db.entitlement) return null;
    const row = await db.entitlement.get(Number(orgzId));
    return row?.projection || null;
  } catch (error) {
    return null;
  }
};

const getAllViewPatientsMrnListFromIdb = async () => {
  const defaultRes = { data: [] };
  // console.log('db',db.patients)
  if (db.allViewPatientsMrnsList === undefined) {
    return defaultRes;
  }
  const allViewPatientsMrnsList = await db.allViewPatientsMrnsList
    .toArray()
    .then(async (idbRes) => {
      if (idbRes !== undefined) {
        const descendingOrderAllMrnsList = idbRes?.[0]?.data?.sort((a, b) => {
          return b.mrn - a.mrn;
        });
        return descendingOrderAllMrnsList;
      } else {
        return [];
      }
    });
  return allViewPatientsMrnsList;
};

const getTotalPatientsCount = async () => {
  let res = 0;

  if (db.totalPatientsCount === undefined) {
    return 0;
  }
  let totalPatientsCount = await db.totalPatientsCount
    .toArray()
    .then(async (res) => {
      if (res !== undefined) {
        return res[0]?.totalPatientsCount;
      } else {
        return 0;
      }
    });
  return totalPatientsCount;
};

const deleteTotalPatientsCount = async () =>
  await db.totalPatientsCount.clear();

//bulk images count

const addOrUpdateTotalBulkImagesCount = async (count) => {
  const users = await db.totalBulkImagesCount
    .put({ totalBulkImagesCount: count })
    .then((res) => {
      return res;
    })
    .catch(function (error) {
      console.log("error while saving count to indexedDb", error);
    });
  return users;
};

const getTotalBulkImagesCount = async () => {
  let res = 0;

  if (db.totalBulkImagesCount === undefined) {
    return 0;
  }
  let totalBulkImagesCount = await db.totalBulkImagesCount
    .toArray()
    .then(async (res) => {
      if (res !== undefined) {
        return res[0]?.totalBulkImagesCount;
      } else {
        return 0;
      }
    });
  return totalBulkImagesCount;
};

const deleteTotalBulkImagesCount = async () =>
  await db.totalBulkImagesCount.clear();

const addOrUpdateTotalAnnotationPatCount = async (count) => {
  const users = await db.totalAnnotationPatientsCount
    .put({ totalAnnotationPatientsCount: count })
    .then((res) => {
      return res;
    })
    .catch(function (error) {
      console.log("error while saving count to indexedDb", error);
    });
  return users;
};

const getTotalAnnotationPatCount = async () => {
  let res = 0;

  if (db.totalAnnotationPatientsCount === undefined) {
    return 0;
  }
  let totalAnnotationPatientsCount = await db.totalAnnotationPatientsCount
    .toArray()
    .then(async (res) => {
      if (res !== undefined) {
        return res[0]?.totalAnnotationPatientsCount;
      } else {
        return 0;
      }
    });
  return totalAnnotationPatientsCount;
};

const deleteTotalAnnotationPatCount = async () =>
  await db.totalAnnotationPatientsCount.clear();

export {
  createDb,
  dbExist,
  getPatients,
  deletePatient,
  savePatient,
  updatePatient,
  getPatientDetailById,
  updatePatientID,
  updateImageDataByIDForBulkUpload,
  //   bulk img list
  saveBulkImages,
  getImages,
  updateImageDataByID,
  getImageById,
  getImagesByStatus,
  deleteImageById,
  bulKDeleteImages,
  // view images
  getViewImages,
  updateViewImageDataByID,
  getViewImageById,
  getViewImagesByStatus,
  deleteViewImageById,
  deleteViewImages,
  swapViewImgList,
  updateUserDetail,
  deleteAllViewImages,
  //report
  saveReportContent,
  getReportContentList,
  saveViewPatients,
  getViewPatients,
  getAllViewPatients,
  updateViewPatient,
  deleteAllViewPatients,
  deleteViewPatientById,
  updateViewPatientID,
  //  user
  refreshIdb,
  saveUserDetail,
  getUsers,
  deleteUser,
  saveUserSettings,
  getUserSettingsFromIdb,
  updateUserSettingsIdb,
  //review patients
  saveReviewPatients,
  getAllReviewPatients,
  deleteAllReviewPatients,
  getReviewPatient,
  updateReviewPatient,
  deleteReviewPatientById,
  //review images
  getReviewImages,
  swapReviewImgList,
  updateReviewImageDataByID,
  deleteAllReviewImages,
  deleteReviewImageById,
  // doctor-annotation-patient
  saveDoctorAnnotationPatient,
  getAllDoctorAnnotationPatientsFromIDB,
  getDoctorAnnotationPatient,
  deleteAllDoctorAnnotationPatients,
  updateDoctorAnnotationPatient,
  deleteDoctorAnnotationPatientById,
  //view patient BU
  saveViewPatientsBu,
  getAllViewPatientsBu,
  deleteViewPatientBuByIds,
  deleteAllViewPatientsBu,
  //TotalBuPatientsCount
  addOrUpdateTotalBuPatientsCount,
  getTotalBuPatientsCount,
  deleteTotalBuPatientsCount,
  //TotalPatientsCount
  addOrUpdateTotalPatientsCount,
  getTotalPatientsCount,
  deleteTotalPatientsCount,
  //TotalBulkImagesCount
  deleteTotalBulkImagesCount,
  getTotalBulkImagesCount,
  addOrUpdateTotalBulkImagesCount,
  //TotalAnnotationsCount
  addOrUpdateTotalAnnotationPatCount,
  getTotalAnnotationPatCount,
  deleteTotalAnnotationPatCount,
  // entitlement
  saveEntitlementProjection,
  getEntitlementProjection,
  // view patient
  addOrUpdateAllViewPatientsMrnsList,
  getAllViewPatientsMrnListFromIdb,
};
